import { Skeleton } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../utils/cn.ts"
import { GLOBAL_MAP_SURFACE_CLASS } from "../global-map-surface.ts"

interface AnalyticsCardProps {
  title: string
  headerExtra?: React.ReactNode
  loading: boolean
  empty?: boolean
  className?: string
  children: React.ReactNode
}

/**
 * The one card shell every Analytics chart/list renders inside — the SAME
 * `GLOBAL_MAP_SURFACE_CLASS` the map rectangle, the KPI tiles and the
 * details column use, so Map/List/Analytics read as three views of one
 * workspace rather than three card designs (spec §15). Per-card
 * loading state (spec §14: "Use card and chart skeletons; preserve
 * dashboard layout") — the card keeps its own footprint while loading, so
 * the grid never reflows as data lands.
 *
 * Error/retry is handled ONE level up, by `AnalyticsTab` itself, not per
 * card: this feature deliberately uses ONE aggregate endpoint (spec §13's
 * own stated preference), so a failure is a single request failing, not N
 * independent per-card failures — spec §14's "partial failure, retry the
 * failed card" literally cannot occur under a single-request architecture.
 * `AnalyticsTab`'s own top-level error state covers this case; documented
 * as a deliberate, disclosed reading of that requirement.
 */
export function AnalyticsCard({
  title,
  headerExtra,
  loading,
  empty,
  className,
  children
}: AnalyticsCardProps) {
  const { t } = useTranslation()

  return (
    <div
      className={cn(
        GLOBAL_MAP_SURFACE_CLASS,
        "flex min-w-0 flex-col gap-4 p-5",
        className
      )}
    >
      <div className="flex min-h-8 items-center justify-between gap-2">
        <h3 className="m-0 text-sm font-semibold text-slate-800">{title}</h3>
        {headerExtra}
      </div>
      {loading ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : empty ? (
        <div className="flex min-h-[120px] items-center justify-center px-4 text-center text-[13px] text-slate-400">
          {t("globalMap.analytics.noData")}
        </div>
      ) : (
        children
      )}
    </div>
  )
}
